'use client'

import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { LucideIcon } from 'lucide-react'
import { useState } from 'react'

interface ToggleIconButtonProps {
  isActive: boolean
  onToggle: () => Promise<void> | void
  icon: LucideIcon
  activeColor: string
  inactiveColor: string
  rippleColor: string
  className?: string
  disabled?: boolean
  iconSize?: number
}

export default function ToggleIconButton({
  isActive,
  onToggle,
  icon: Icon,
  activeColor,
  inactiveColor,
  rippleColor,
  className,
  disabled = false,
  iconSize = 16,
}: ToggleIconButtonProps) {
  const [isAnimating, setIsAnimating] = useState(false)

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (disabled || isAnimating) return

    setIsAnimating(true)
    try {
      await onToggle()
    } finally {
      setTimeout(() => setIsAnimating(false), 300)
    }
  }

  return (
    <motion.button
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'rounded-full bg-white/80 p-1.5 shadow-sm backdrop-blur-sm transition-all duration-200',
        'hover:bg-white hover:shadow-md',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      whileTap={{ scale: 0.9 }}
      whileHover={{ scale: 1.05 }}
    >
      {isAnimating && (
        <motion.div
          className={cn('absolute inset-0 rounded-full', rippleColor)}
          initial={{ scale: 0.8, opacity: 0.8 }}
          animate={{ scale: 1.5, opacity: 0 }}
          transition={{ duration: 0.3 }}
        />
      )}

      <Icon
        size={iconSize}
        className={cn('transition-colors duration-200', isActive ? activeColor : inactiveColor)}
        strokeWidth={2}
      />
    </motion.button>
  )
}
